import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/media";
import { X, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Star, Upload, Camera, Trash2, Plus } from "lucide-react";

// Re-export for backwards compatibility with AdminVehicles
export { resolveImg } from "@/lib/media";

const TYPES = ["ev", "hybrid", "petrol", "commercial"];

const EMPTY = {
  slug: "", make: "", model: "", year: new Date().getFullYear(),
  type: "petrol", price_ghs: 0, tagline: "",
  mileage_km: 0, engine: "", seats: 5, transmission: "",
  battery: "", range_km: null, fuel_economy: "",
  images: [], highlights: [""], specs: [{ label: "", value: "" }],
  description: "", featured: false,
};

function toSlug(make, model, year) {
  return `${make} ${model} ${year || ""}`.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

// Resolve an image path (data URL, external URL, or /api/media/{id}) to a full src
// (moved to @/lib/media; kept as no-op section marker)

export default function VehicleForm({ initial, onClose, onSaved }) {
  const [v, setV] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!isEdit && v.make && v.model) {
      setV((prev) => ({ ...prev, slug: toSlug(prev.make, prev.model, prev.year) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.make, v.model, v.year, isEdit]);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/media", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setV((s) => ({ ...s, images: [...(s.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const moveImage = (idx, dir) => {
    setV((s) => {
      const imgs = [...s.images];
      const j = idx + dir;
      if (j < 0 || j >= imgs.length) return s;
      [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
      return { ...s, images: imgs };
    });
  };
  const makeCover = (idx) => {
    setV((s) => {
      if (idx === 0) return s;
      const imgs = [...s.images];
      const [pick] = imgs.splice(idx, 1);
      imgs.unshift(pick);
      return { ...s, images: imgs };
    });
  };
  const makeLast = (idx) => {
    setV((s) => {
      if (idx === s.images.length - 1) return s;
      const imgs = [...s.images];
      const [pick] = imgs.splice(idx, 1);
      imgs.push(pick);
      return { ...s, images: imgs };
    });
  };
  const removeImage = (idx) => setV((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const setHighlight = (i, val) => setV((s) => {
    const h = [...s.highlights]; h[i] = val; return { ...s, highlights: h };
  });
  const addHighlight = () => setV((s) => ({ ...s, highlights: [...s.highlights, ""] }));
  const removeHighlight = (i) => setV((s) => ({ ...s, highlights: s.highlights.filter((_, k) => k !== i) }));

  const setSpec = (i, key, val) => setV((s) => {
    const sp = [...s.specs]; sp[i] = { ...sp[i], [key]: val }; return { ...s, specs: sp };
  });
  const addSpec = () => setV((s) => ({ ...s, specs: [...s.specs, { label: "", value: "" }] }));
  const removeSpec = (i) => setV((s) => ({ ...s, specs: s.specs.filter((_, k) => k !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...v,
        year: Number(v.year),
        price_ghs: Number(v.price_ghs),
        mileage_km: Number(v.mileage_km),
        seats: Number(v.seats),
        range_km: v.range_km === "" || v.range_km == null ? null : Number(v.range_km),
        highlights: (v.highlights || []).filter((h) => h && h.trim()),
        specs: (v.specs || []).filter((s) => s.label && s.value),
      };
      if (!payload.slug) payload.slug = toSlug(payload.make, payload.model, payload.year);
      if (!payload.images?.length) throw new Error("Upload at least one image.");
      if (isEdit) {
        await api.put(`/admin/vehicles/${initial.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Vehicle published");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" data-testid="vehicle-form-modal">
      <div className="relative w-full max-w-5xl bg-[#050814] border border-white/10 my-8">
        <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-6 border-b border-white/10 bg-[#050814]/95 backdrop-blur">
          <div>
            <div className="overline text-[#C5A059]">{isEdit ? "Edit Vehicle" : "New Vehicle"}</div>
            <div className="font-display text-2xl mt-1">{v.make || "—"} {v.model}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="vehicle-form-close"><X size={20}/></button>
        </div>

        <div className="p-8 space-y-10">
          {/* Basic fields */}
          <div className="grid md:grid-cols-3 gap-6">
            <div><label className="field-label">Make *</label><input className="field" data-testid="vf-make" value={v.make} onChange={(e)=>set("make", e.target.value)} /></div>
            <div><label className="field-label">Model *</label><input className="field" data-testid="vf-model" value={v.model} onChange={(e)=>set("model", e.target.value)} /></div>
            <div><label className="field-label">Year *</label><input type="number" className="field" data-testid="vf-year" value={v.year} onChange={(e)=>set("year", e.target.value)} /></div>
            <div>
              <label className="field-label">Type *</label>
              <select className="field bg-[#050814]" data-testid="vf-type" value={v.type} onChange={(e)=>set("type", e.target.value)}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="field-label">Price (GHS) *</label><input type="number" className="field" data-testid="vf-price" value={v.price_ghs} onChange={(e)=>set("price_ghs", e.target.value)} /></div>
            <div><label className="field-label">Mileage (km) *</label><input type="number" className="field" data-testid="vf-mileage" value={v.mileage_km} onChange={(e)=>set("mileage_km", e.target.value)} /></div>
            <div><label className="field-label">Engine</label><input className="field" data-testid="vf-engine" value={v.engine} onChange={(e)=>set("engine", e.target.value)} /></div>
            <div><label className="field-label">Transmission</label><input className="field" data-testid="vf-trans" value={v.transmission} onChange={(e)=>set("transmission", e.target.value)} /></div>
            <div><label className="field-label">Seats</label><input type="number" className="field" value={v.seats} onChange={(e)=>set("seats", e.target.value)} /></div>
            <div><label className="field-label">Battery</label><input className="field" value={v.battery || ""} onChange={(e)=>set("battery", e.target.value)} placeholder="e.g. 26.7 kWh"/></div>
            <div><label className="field-label">EV Range (km)</label><input type="number" className="field" value={v.range_km || ""} onChange={(e)=>set("range_km", e.target.value)} /></div>
            <div><label className="field-label">Fuel Economy</label><input className="field" value={v.fuel_economy || ""} onChange={(e)=>set("fuel_economy", e.target.value)} placeholder="e.g. 8.3L / 100km"/></div>
          </div>

          <div><label className="field-label">Slug (auto)</label><input className="field" value={v.slug} onChange={(e)=>set("slug", e.target.value)} data-testid="vf-slug"/></div>

          <div><label className="field-label">Tagline</label><input className="field" value={v.tagline} onChange={(e)=>set("tagline", e.target.value)} data-testid="vf-tagline"/></div>

          <div><label className="field-label">Description</label>
            <textarea className="field" rows={4} value={v.description} onChange={(e)=>set("description", e.target.value)} data-testid="vf-desc"/>
          </div>

          <label className="flex items-center gap-3 text-white/80 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-[#C5A059]" data-testid="vf-featured" checked={!!v.featured} onChange={(e)=>set("featured", e.target.checked)} />
            <span>Featured on Home page</span>
          </label>

          {/* Highlights */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="field-label">Highlights</div>
              <button type="button" onClick={addHighlight} className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059] hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
            </div>
            <div className="space-y-3">
              {v.highlights.map((h, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className="field flex-1" value={h} onChange={(e)=>setHighlight(i, e.target.value)} placeholder="e.g. Panoramic sunroof"/>
                  <button type="button" onClick={()=>removeHighlight(i)} className="text-white/40 hover:text-red-400 p-2"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>

          {/* Specs */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="field-label">Specifications</div>
              <button type="button" onClick={addSpec} className="text-[10px] tracking-[0.24em] uppercase text-[#C5A059] hover:underline flex items-center gap-1"><Plus size={12}/> Add</button>
            </div>
            <div className="space-y-3">
              {v.specs.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className="field col-span-4" placeholder="Label" value={s.label} onChange={(e)=>setSpec(i, "label", e.target.value)}/>
                  <input className="field col-span-7" placeholder="Value" value={s.value} onChange={(e)=>setSpec(i, "value", e.target.value)}/>
                  <button type="button" onClick={()=>removeSpec(i)} className="col-span-1 text-white/40 hover:text-red-400"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="field-label">Images ({v.images.length}) — first image is the cover</div>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e)=>{ uploadFiles(e.target.files); e.target.value=""; }} data-testid="vf-file-input"/>
                <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e)=>{ uploadFiles(e.target.files); e.target.value=""; }} data-testid="vf-cam-input"/>
                <button type="button" onClick={()=>fileRef.current?.click()} className="btn-editorial" data-testid="vf-btn-upload">
                  <Upload size={14}/> {uploading ? "Uploading…" : "From device"}
                </button>
                <button type="button" onClick={()=>camRef.current?.click()} className="btn-editorial" data-testid="vf-btn-camera">
                  <Camera size={14}/> Camera
                </button>
              </div>
            </div>
            {v.images.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center text-white/50 text-sm">
                No images yet. Upload from device or take a photo with your camera.
              </div>
            ) : (
              <>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 mb-3">
                  Drag order · Left = cover · Right = last · Use ← → to swap positions, ★ to promote to cover
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {v.images.map((im, i) => {
                    const isCover = i === 0;
                    const isLast = i === v.images.length - 1;
                    return (
                      <div
                        key={`${im}-${i}`}
                        className={`relative border overflow-hidden ${isCover ? "border-[#C5A059]" : "border-white/10"}`}
                        data-testid={`vf-image-${i}`}
                      >
                        {/* Position chip */}
                        <div className="absolute top-2 left-2 z-10">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.22em] uppercase ${
                            isCover ? "bg-[#C5A059] text-[#050814]" : "bg-[#050814]/80 text-white border border-white/20"
                          }`}>
                            {isCover ? "Cover · 01" : `Pos ${String(i + 1).padStart(2, "0")}`}
                          </span>
                        </div>
                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center bg-[#050814]/80 border border-white/20 text-white hover:bg-red-500/80 hover:border-red-400 transition"
                          data-testid={`vf-image-remove-${i}`}
                          aria-label="Remove image"
                        >
                          <Trash2 size={12} />
                        </button>

                        {/* Image */}
                        <div className="aspect-[4/3] bg-[#0A1128]">
                          <img src={resolveImg(im)} alt="" className="w-full h-full object-cover" />
                        </div>

                        {/* Reorder toolbar — always visible */}
                        <div className="flex items-stretch border-t border-white/10 bg-[#050814]">
                          <button
                            type="button"
                            disabled={isCover}
                            onClick={() => moveImage(i, -1)}
                            className="flex-1 py-2.5 text-white/80 hover:text-[#C5A059] disabled:opacity-25 disabled:cursor-not-allowed border-r border-white/10 flex items-center justify-center gap-1 text-[10px] tracking-[0.2em] uppercase"
                            data-testid={`vf-image-left-${i}`}
                            title="Swap with previous"
                          >
                            <ArrowLeft size={13}/> Left
                          </button>
                          <button
                            type="button"
                            disabled={isCover}
                            onClick={() => makeCover(i)}
                            className="flex-1 py-2.5 text-white/80 hover:text-[#C5A059] disabled:opacity-25 disabled:cursor-not-allowed border-r border-white/10 flex items-center justify-center gap-1 text-[10px] tracking-[0.2em] uppercase"
                            data-testid={`vf-image-cover-${i}`}
                            title="Set as cover"
                          >
                            <Star size={12} fill={isCover ? "#C5A059" : "none"}/> Cover
                          </button>
                          <button
                            type="button"
                            disabled={isLast}
                            onClick={() => moveImage(i, 1)}
                            className="flex-1 py-2.5 text-white/80 hover:text-[#C5A059] disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center gap-1 text-[10px] tracking-[0.2em] uppercase"
                            data-testid={`vf-image-right-${i}`}
                            title="Swap with next"
                          >
                            Right <ArrowRight size={13}/>
                          </button>
                        </div>
                        {!isCover && !isLast && (
                          <button
                            type="button"
                            onClick={() => makeLast(i)}
                            className="w-full py-2 text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-[#C5A059] border-t border-white/10 bg-[#050814]"
                            data-testid={`vf-image-last-${i}`}
                          >
                            Move to end
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-3 px-8 py-6 border-t border-white/10 bg-[#050814]/95 backdrop-blur">
          <button onClick={onClose} className="btn-editorial" data-testid="vf-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-editorial btn-editorial-gold disabled:opacity-60" data-testid="vf-save">
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Publish Vehicle")}
          </button>
        </div>
      </div>
    </div>
  );
}
